import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'react-flexbox-grid';
import { LabelPill } from '@saucelabs/sl-component-label';
import Modal from '@saucelabs/ds-modal';
import { modalConnectV2, modalActions } from '@saucelabs/sl-container-modal';
import { IconCheckmarkCircleOutline, IconPlusCircleOutline, IconStartupOutline, IconDevicePhoneOutline, IconEnterpriseOutline, IconPlusFill, IconExternalFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';
import Button from '@saucelabs/ds-button';
import { useDispatch } from 'react-redux';

import { FeatureList } from './Plans/FeatureList';
import { Price, PriceAddon } from './Plans/Price';
import Styles from './PlanComparisonModal.styles';
import { MOBILE_QUALITY_COMPARISON_MODAL } from '../constants';

interface ButtonProps {
  handleToggle: () => void,
  hasMobileQualityInCart: boolean,
  hasMobileQualitySubscription: boolean,
  isButtonBlocked: boolean,
}

const StartupPlanButton: React.FC<ButtonProps> = ({
  handleToggle,
  hasMobileQualityInCart,
  hasMobileQualitySubscription,
  isButtonBlocked,
}) => {
  if (hasMobileQualitySubscription) {
    return <span>You&apos;re currently subscribed to this plan</span>;
  }

  return (
    <Button
      block
      className="w-full mt-0"
      color={hasMobileQualityInCart ? 'secondary' : 'primary'}
      data-qa="mobile-quality-modal-plan-toggle-button"
      disabled={isButtonBlocked}
      onPress={handleToggle}
      prefixIcon={!hasMobileQualityInCart ? <IconPlusFill /> : undefined}
      variant={hasMobileQualityInCart ? 'outline' : 'contained'}
    >
      {hasMobileQualityInCart ? 'Remove Plan' : 'Add Plan'}
    </Button>
  );
};

export interface Props {
  handleTogglePlan: (hasMobileQualityInCart: boolean) => void,
  hasMobileQualityInCart: boolean,
  hasMobileQualitySubscription: boolean,
  planPrice: number,
  redirectOnSuccess: string | undefined,
}

const headerIconClassnames = 'w-3 h-3 fill-current text-gray-500';
const columnClassnames = `${Styles['plan-column']} flex flex-col justify-between`;

const MobileQualityPlanCompareModal: React.FC<Props> = ({
  handleTogglePlan,
  hasMobileQualityInCart,
  hasMobileQualitySubscription,
  planPrice,
  redirectOnSuccess,
}) => {
  const [isButtonBlocked, setButtonBlocked] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const handleToggle = useCallback(
    () => {
      setButtonBlocked(true);
      setTimeout(() => {
        handleTogglePlan(hasMobileQualityInCart);
        dispatch(modalActions.hideModal());
        setButtonBlocked(false);
        if (redirectOnSuccess) history.push(redirectOnSuccess);
      }, 300);
    },
    [hasMobileQualityInCart],
  );

  return (
    <Modal
      data-qa="mobile-quality-plan-compare-modal"
      emphasis="low"
      size="large"
      title="Mobile Beta Testing - TestFairy"
    >
      <Row>
        <Col
          className={Styles['plan-column']}
          xs={4}
        >
          <div>
            <Typography
              className={`${Styles['plan-heading']} flex items-center`}
              disableSpacing
              variant="heading-4"
            >
              <IconDevicePhoneOutline className={headerIconClassnames} />
              Free
            </Typography>
            <FeatureList.Header>
              Basic App Distribution
            </FeatureList.Header>
            <FeatureList isColumn>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Basic App Distribution
              </li>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                5 Apps
              </li>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                100 Beta Testers
              </li>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                30 Days Data Retention
              </li>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                100 Video Sessions / Month
              </li>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Ad-hoc iOS certificates
              </li>
            </FeatureList>
          </div>
        </Col>
        <Col
          className={Styles['plan-column']}
          xs={4}
        >
          <div>
            <Typography
              className={`${Styles['plan-heading']} flex items-center`}
              disableSpacing
              variant="heading-4"
            >
              <IconStartupOutline className={headerIconClassnames} />
              Startup
              <LabelPill variant="green">Most Popular</LabelPill>
            </Typography>
            <FeatureList.Header>
              Beta Testing with Video Recording
            </FeatureList.Header>
            <FeatureList isColumn>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                <strong>Includes everything in Free, plus:</strong>
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                50 Apps
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                500 Beta Testers
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                180 Days Data Retention
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                10,000 Video Sessions / Month
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                JIRA, Trello, Slack Integration
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Enterprise / Ad-Hoc iOS Certificates
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                In-App Feedback
              </li>
            </FeatureList>
          </div>
        </Col>
        <Col
          className={Styles['plan-column']}
          xs={4}
        >
          <div>
            <Typography
              className={`${Styles['plan-heading']} flex items-center`}
              disableSpacing
              variant="heading-4"
            >
              <IconEnterpriseOutline className={headerIconClassnames} />
              Enterprise
            </Typography>
            <FeatureList.Header>
              Enterprise-Grade Security Compliance
            </FeatureList.Header>
            <FeatureList isColumn>
              <li className="flex">
                <IconCheckmarkCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Unlimited Admins, Beta Testers and Apps
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Enterprise App Distribution
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Private Storage
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                1 Year Data Retention
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Unlimited Video Sessions
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Security Compliance
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Single Sign-On Integration (SAML 2.0)
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                Data Encryption with your Private Keys
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                HIPPA*, GDPR* Custom DPA* (Contact for Details)
              </li>
              <li className="flex">
                <IconPlusCircleOutline className="fill-current text-green-600 mt-0.5 h-2 w-2" />
                JIRA Server/Cloud Integration
              </li>
            </FeatureList>
          </div>
        </Col>
      </Row>
      <Row>
        <Col
          className={columnClassnames}
          xs={4}
        >
          <Price className="mt-0 mb-2">
            $0
            <PriceAddon> / month</PriceAddon>
          </Price>
          <div className="my-1">
            {!hasMobileQualitySubscription && (
              <span className="font-semibold text-gray-600">You&apos;re currently subscribed to this plan</span>
            )}
          </div>
        </Col>
        <Col
          className={columnClassnames}
          xs={4}
        >
          <Price className="mt-0 mb-2">
            {`$${planPrice}`}
            <PriceAddon> / month</PriceAddon>
          </Price>
          <div>
            <StartupPlanButton
              handleToggle={handleToggle}
              hasMobileQualityInCart={hasMobileQualityInCart}
              hasMobileQualitySubscription={hasMobileQualitySubscription}
              isButtonBlocked={isButtonBlocked}
            />
          </div>
        </Col>
        <Col
          className={columnClassnames}
          xs={4}
        >
          <Button
            block
            className="mt-4 w-full mt-auto"
            component="a"
            href="https://www.testfairy.com/contact_us.php"
            postfixIcon={<IconExternalFill />}
            rel="noopener noreferrer"
            target="_blank"
            variant="outline"
          >
            Contact Sales
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default modalConnectV2(MOBILE_QUALITY_COMPARISON_MODAL, MobileQualityPlanCompareModal);
export { MobileQualityPlanCompareModal };
